import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import ActionSettings from '../../../library/SimpleControls/ActionSettings';
import * as api from './RoleCapabilityService';
import RbacPageShell from './RbacPageShell';

const emptyForm = { capabilityKey: '', module: '', description: '' };

const CapabilitiesPage = () => {
    const [rows, setRows] = useState<api.CapabilityRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('');
    const [form, setForm] = useState(emptyForm);
    const [editId, setEditId] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.listCapabilities(true);
            setRows(res.data.capabilities || []);
        } catch (err) {
            setError(api.errorMessage(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(
            (r) =>
                r.capabilityKey.toLowerCase().includes(q) ||
                r.module.toLowerCase().includes(q) ||
                (r.description || '').toLowerCase().includes(q)
        );
    }, [rows, filter]);

    const openCreate = () => {
        setEditId(null);
        setForm(emptyForm);
        setError('');
        setShowModal(true);
    };

    const openEdit = (row: api.CapabilityRow) => {
        setEditId(row.id);
        setForm({
            capabilityKey: row.capabilityKey,
            module: row.module,
            description: row.description || '',
        });
        setError('');
        setShowModal(true);
    };

    const onSave = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            if (editId == null) {
                await api.createCapability({
                    capabilityKey: form.capabilityKey.trim().toLowerCase(),
                    module: form.module.trim().toLowerCase(),
                    description: form.description.trim() || undefined,
                });
            } else {
                await api.updateCapability(editId, {
                    module: form.module.trim().toLowerCase(),
                    description: form.description.trim() || null,
                });
            }
            setShowModal(false);
            await load();
        } catch (err) {
            setError(api.errorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const toggleActive = async (row: api.CapabilityRow) => {
        const msg = row.isActive
            ? 'Are you sure you want to Deactivate this record?'
            : 'Are you sure you want to Activate this record?';
        if (!window.confirm(msg)) return;
        setBusy(true);
        setError('');
        try {
            if (row.isActive) await api.deleteCapability(row.id);
            else await api.updateCapability(row.id, { isActive: true });
            await load();
        } catch (err) {
            setError(api.errorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const capabilityActions = (row: api.CapabilityRow) => [
        'EDIT',
        row.isActive ? 'DEACTIVATE' : 'ACTIVATE',
    ];

    const handleAction = (row: api.CapabilityRow, action: string) => {
        switch (action) {
            case 'EDIT':
                openEdit(row);
                break;
            case 'DEACTIVATE':
            case 'ACTIVATE':
                if (!busy) toggleActive(row);
                break;
            default:
                break;
        }
    };

    return (
        <RbacPageShell
            title="Manage capabilities"
            subtitle="Catalog of permission keys (module.resource.action)."
            actions={
                <button type="button" className="btn btn-green" onClick={openCreate}>
                    <span>Add capability</span>
                </button>
            }
        >
            {error && !showModal ? (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            ) : null}

            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 filterSearch gap-2">
                <span className="font-14">Found {filtered.length} capabilities</span>
                <div style={{ minWidth: 240, maxWidth: 360 }} className="flex-grow-1 flex-md-grow-0">
                    <input
                        className="form-control"
                        placeholder="Search by key, module or description…"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                </div>
            </div>

            {loading ? (
                <p className="mb-0">Loading…</p>
            ) : (
                <div className="table-responsive attendee_dtl" style={{ maxHeight: '65vh' }}>
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Capability key</th>
                                <th>Module</th>
                                <th>Description</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((row) => (
                                <tr key={row.id}>
                                    <td>{row.capabilityKey}</td>
                                    <td>{row.module}</td>
                                    <td>{row.description || '—'}</td>
                                    <td style={{ color: row.isActive ? 'green' : 'red' }}>
                                        {row.isActive ? 'Active' : 'De-active'}
                                    </td>
                                    <td>
                                        <ActionSettings
                                            id={row}
                                            actions={capabilityActions(row)}
                                            onClick={handleAction}
                                        />
                                    </td>
                                </tr>
                            ))}
                            {!filtered.length ? (
                                <tr>
                                    <td colSpan={5} className="text-muted">
                                        No capabilities match the search.
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="text-muted font-13 mt-2 mb-0">
                capability_key cannot be changed after create. Deactivate is a soft delete.
            </p>

            {showModal ? (
                <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ background: 'rgba(0,0,0,.4)' }}>
                    <div className="modal-dialog modal-dialog-centered max-600 mx-auto">
                        <div className="modal-content">
                            <div className="modal-header p-4 height-70 border-0 bg-gray-f2">
                                <h5 className="modal-title">
                                    {editId == null ? 'Add capability' : 'Edit capability'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-small"
                                    aria-label="Close"
                                    onClick={() => setShowModal(false)}
                                />
                            </div>
                            <div className="modal-body p-4">
                                {error ? (
                                    <div className="alert alert-danger py-2" role="alert">
                                        {error}
                                    </div>
                                ) : null}
                                <form className="needs-validation" noValidate onSubmit={onSave}>
                                    <div className="row g-4">
                                        <div className="col-md-12">
                                            <label className="form-label">Capability key</label>
                                            <input
                                                className="form-control flex-1"
                                                placeholder="module.resource.action"
                                                value={form.capabilityKey}
                                                disabled={editId != null}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        capabilityKey: e.target.value.toLowerCase(),
                                                    })
                                                }
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Module</label>
                                            <input
                                                className="form-control flex-1"
                                                value={form.module}
                                                onChange={(e) =>
                                                    setForm({ ...form, module: e.target.value.toLowerCase() })
                                                }
                                                required
                                            />
                                        </div>
                                        <div className="col-md-12">
                                            <label className="form-label">Description</label>
                                            <textarea
                                                className="form-control"
                                                style={{ height: 90 }}
                                                value={form.description}
                                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="row justify-content-center align-items-center mt-4">
                                        <div className="col-auto">
                                            <button
                                                type="button"
                                                className="btn btn-cancel"
                                                onClick={() => setShowModal(false)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                        <div className="col-auto">
                                            <button type="submit" className="btn btn-green max-135" disabled={busy}>
                                                <span>{editId == null ? 'Save' : 'Update'}</span>
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </RbacPageShell>
    );
};

export default CapabilitiesPage;
